import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as ServerOperations from "@/Common/Operations/operations.server";

interface ISingleSelectAutoCompleteProps {
    id?: string;
    valid?: boolean;
    componentId: string;
    selectedItems: Immutable.List<KeyValuePair<string, string>>;
    selectionOp?: number;
    clearOp?: number;
    disabled?: boolean;
    focusOnLoad?: boolean;
    useblurevent?: boolean;
    closeDropDownAfterSelection?: boolean;
    changedTextValueEvent?: number;
    displayedTextValue?: string; // all input and selection is set here
    dataCy?: string;

    onChangeText?: (text) => void;
    onSelecting?: (kvp: KeyValuePair<string, string>) => void;
    onClear?: () => void;
}

class SingleSelectAutoCompleteState {
    public text: string;
    public expanded: boolean;
    public infocus: boolean;
    public items: KeyValuePair<string, string>[];
    public selectedItemList: KeyValuePair<string, string>[];

    public static getDefault(props: ISingleSelectAutoCompleteProps): SingleSelectAutoCompleteState {
        const state = new SingleSelectAutoCompleteState();
        state.infocus = props.focusOnLoad === true;
        state.expanded = false;
        state.items = [];
        state.text = props.displayedTextValue || "";
        state.selectedItemList = props.selectedItems.toArray();

        return state;
    }
}

/**
 *
    This component allows display of an dropdown style control with choices being autofetched server-side
    At the moment it supports 1 selection only.
    Update the text value of your model using the selected & changed operations.
 */

class SingleSelectAutoComplete extends BaseReactComponent<ISingleSelectAutoCompleteProps, SingleSelectAutoCompleteState> {
    private width: string;
    private control: JQuery;
    private debounceMs: number;
    private minQueryLengthSearchChars: number;
    private blurBuffer: number;
    private timeoutReference: any;

    constructor(props: ISingleSelectAutoCompleteProps) {
        super(props);

        this.width = "";
        this.debounceMs = 225;
        this.minQueryLengthSearchChars = 2;

        this.state = SingleSelectAutoCompleteState.getDefault(this.props);
    }

    public componentDidMount(): void {
        if (this.props.useblurevent == null || this.props.useblurevent === false) {
            $(window).on("click.pc", () => this.onWindowClick());
        }
        this.setWidth(this.props);
        if (this.props.focusOnLoad)
            $("#" + this.props.componentId).find('input[type="text"]').focus();
    }

    public componentDidUpdate(prevProps: ISingleSelectAutoCompleteProps): void {
        if (this.props.id != prevProps.id)
            this.setState(SingleSelectAutoCompleteState.getDefault(this.props));
    }

    public render(): JSX.Element {
        this.setWidth(this.props);

        let dropdownPart: JSX.Element;
        if (!this.state.expanded || this.state.items.length === 0) { dropdownPart = null; }
        else {
            dropdownPart = (
                <div style={{ width: this.width }} className="ui-wrp-b">
                    <ul data-role="available-options">
                        {this.state.items && this.state.items.map((i, idx) => {
                            const isSelected = this.state.selectedItemList.some(d => d.Key === i.Key);
                            let css = "";
                            if (isSelected && !i.Active) {
                                css = "selected";
                            }
                            else if (isSelected && i.Active) {
                                css = "selected pending";
                            }
                            else if (!isSelected && i.Active) { css = "pending"; }
                            return <li className={css} key={i.Key + "_" + idx} onClick={e => this.onItemClick(i, e)}><span>{i.Value}</span></li>;
                        })}
                    </ul>
                </div>
            );
        }

        let bottomPad = null;

        if (this.state.infocus === false && this.state.items.length > 0) {
            bottomPad = <div style={{ height: "3px", clear: "both" }} />;
        }

        return (
            <div id={this.props.componentId} className={this.props.valid === false ? "myle-ui-autocomplete invalid" : "myle-ui-autocomplete"} onClick={e => this.onContainerClick(e)}>
                <div className="ui-wrp-a clearfix">

                    <input
                        onKeyDown={e => this.inputKeyDown(e)}
                        onBlur={() => this.onBlur()}
                        onChange={(e: any) => this.searchTextChanged(e.target.value)}
                        disabled={this.props.disabled}
                        value={this.state.text}
                        type="text"
                        className="ui-text-input fullWidth"
                        style={{ paddingLeft: "5px" }}
                        data-cy={this.props.dataCy}
                    />
                    {bottomPad}
                </div>

                {dropdownPart}
            </div>
        );
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        if (this.props.useblurevent == null || this.props.useblurevent === false) {
            $(window).off("click.pc");
        }
    }

    private setWidth(props: ISingleSelectAutoCompleteProps): void {
        this.control = $("#" + props.componentId);
        this.width = (this.control.width() + 10) + "px";
    }

    private onContainerClick(e: React.MouseEvent): void {
        e.stopPropagation();
        window.setTimeout(() => {
            $("#" + this.props.componentId).find('input[type="text"]').focus();
        }, 50);
    }

    private onBlur(): void {
        if (this.props.useblurevent === true)
            this.onControlLeave();
    }

    private onWindowClick(): void {
        this.mutateState(s => {
            s.expanded = false;
            if (this.state.selectedItemList.length > 0)
                s.infocus = false;
        });
    }

    private onControlLeave(): void {
        this.blurBuffer = window.setTimeout(() => this.mutateState(s => {
            s.expanded = false;
            if (this.state.selectedItemList.length > 0)
                s.infocus = false;
            s.items = this.state.items;
            s.text = this.state.text;
        }), 300); // render with dropdown
    }

    private inputKeyDown(e: React.KeyboardEvent): void {
        if (e.keyCode === 8) {
            // delete last item
            if (e.ctrlKey) {
                if (this.state.text.length === 0) {
                    if (this.props.clearOp)
                        sessionStore.dispatch(this.props.clearOp);
                    else if (this.props.onClear)
                        this.props.onClear();
                }
            }
            else {
                if (this.state.selectedItemList && this.state.selectedItemList.length > 0 && this.state.text.length === 0) {
                    let size = this.state.selectedItemList.length;
                    let kvp = this.state.selectedItemList[size - 1];
                    kvp.Active = false;

                    if (this.props.selectionOp)
                        sessionStore.dispatch(this.props.selectionOp, { value: kvp });
                    else if (this.props.onSelecting)
                        this.props.onSelecting(kvp);
                }
            }
        }

        // up
        if (e.keyCode === 38) { e.preventDefault(); this.previousItem(); }

        // down
        if (e.keyCode === 40) { e.preventDefault(); this.nextItem(); }

        // enter
        if (e.keyCode === 13) { this.addSeletedItem(); }

        // escape
        if (e.keyCode === 27) {
            this.clearState();
        }
    }

    // keyboard triggered
    private nextItem(): void {
        let collection = this.state.items;
        let idx = this.indexOfSelectedItem();
        if (idx === -1) {
            collection[0].Active = true;
            this.updateStateWithNewItemList(collection, true);
            return;
        }
        if (idx + 1 < collection.length) {
            collection[idx].Active = false;
            collection[idx + 1].Active = true;
            this.updateStateWithNewItemList(collection, true);
        }
        this.scrollDown();
    }

    private scrollUp(): void {
        let container = $("#" + this.props.componentId);
        let scrollContainer = container.find("div.ui-wrp-b");
        let maxHeight = +scrollContainer.css("max-height").replace("px", "");
        let ul = container.find('ul[data-role="available-options"]');
        let sct = scrollContainer.scrollTop();
        let hightlightedItemPosition = (ul.find("li.pending").offset().top - ul.offset().top);
        let pr = Math.round((hightlightedItemPosition / maxHeight) * 100);
        if (pr < 100) { scrollContainer.scrollTop(sct - 50); }
    }

    private scrollDown(): void {
        let container = $("#" + this.props.componentId);
        let scrollContainer = container.find("div.ui-wrp-b");
        let maxHeight = +scrollContainer.css("max-height").replace("px", "");

        let ul = container.find('ul[data-role="available-options"]');
        let sct = scrollContainer.scrollTop();
        let hightlightedItemPosition = (ul.find("li.pending").offset().top - ul.offset().top);

        let pr = Math.round((hightlightedItemPosition / maxHeight) * 100);
        if (pr > 50) { scrollContainer.scrollTop(sct + 50); }
    }

    // keyboard triggered
    private previousItem(): void {
        let idx = this.indexOfSelectedItem();
        if (idx === -1) {
            this.collapseDropdown();
            return;
        }

        let collection = this.state.items;
        if (idx === 0) {
            collection[0].Active = false;
            this.updateStateWithNewItemList(collection, false);
            return;
        }

        if (idx > 0) {
            collection[idx].Active = false;
            idx--;
            collection[idx].Active = true;
            this.updateStateWithNewItemList(collection, true);
        }

        else if (idx === 1) {
            collection[0].Active = true;
            collection[1].Active = false;
            this.updateStateWithNewItemList(collection, false);
        }

        this.scrollUp();
    }

    private updateStateWithNewItemList(list: KeyValuePair<string, string>[], expand: boolean): void {
        this.mutateState(s => {
            s.expanded = expand;
            s.items = list.map(i => i);
        });
    }

    private indexOfSelectedItem(): number {
        if (this.state.items.length === 0) return -1;
        for (let i = 0; i < this.state.items.length; i++) {
            if (this.state.items[i].Active) { return i; }
        } return -1;
    }

    private addSeletedItem(): void {
        let idx = this.indexOfSelectedItem();
        if (idx >= 0)
            this.onItemClick(this.state.items[idx], null);
    }

    private onItemClick(item: KeyValuePair<string, string>, e: React.MouseEvent): void {
        if (e != null)
            e.stopPropagation();

        if (this.blurBuffer != null) {
            clearTimeout(this.blurBuffer);
            this.blurBuffer = null;
        }

        this.mutateState(
            s => {
                s.text = item.Value;
            },
            () => {
                if (this.props.closeDropDownAfterSelection)
                    this.clearState();
            }
        );

        if (this.props.selectionOp) {
            sessionStore.dispatch(this.props.selectionOp, { value: item });
        }
        else if (this.props.onSelecting) {
            this.props.onSelecting(item);
        }
    }

    private searchTextChanged(text: string): void {
        this.mutateState(s => s.text = text);

        if (this.timeoutReference != null) {
            clearTimeout(this.timeoutReference);
            this.timeoutReference = null;
        }

        this.timeoutReference = window.setTimeout(() => {
            if (this.props.changedTextValueEvent) {
                sessionStore.dispatch(this.props.changedTextValueEvent, { value: text });
            }
            else if (this.props.onChangeText) {
                this.props.onChangeText(text);
            }

            this.invokeDebouncedSearch(text);
        }, this.debounceMs);
    }

    private invokeDebouncedSearch(text: string): void {
        if (text == null)
            return;

        if (text.length < this.minQueryLengthSearchChars)
            return;

        let data = {
            SearchTerm: this.props.displayedTextValue,
            MaxResults: 20
        };

        Client.Post(ServerOperations.Server.Myle_GetRecallsTypes, data, response => {
            const results = this.convertDescriptionResponseToKvp(response);
            this.mutateState(s => {
                s.expanded = true;
                s.items = results;
            });
        });
    }

    private collapseDropdown(): void {
        this.mutateState(s => s.expanded = false);
    }

    private clearState(): void {
        this.mutateState(s => {
            s.expanded = false;
            s.infocus = false;
            s.items = [];
        });
    }

    private convertDescriptionResponseToKvp(response: any): KeyValuePair<string, string>[] {
        return response.RecallsTypes && response.RecallsTypes.map(x => new KeyValuePair<string, string>(x, x));
    }
}

export { SingleSelectAutoComplete };
